import type { GroupBy } from '../fixtures/domain.js';
import { Switch } from '../components/Switch.js';
import styles from './ViewPanel.module.css';

// The default first, so the segment reads as "Person, or instead…" rather than
// opening on the one value that turns the default off. See DEFAULT_GROUP_BY.
const GROUPS: { value: GroupBy; label: string }[] = [
  { value: 'person', label: 'Person' },
  { value: 'job', label: 'Job' },
  { value: 'none', label: 'None' },
];

interface ViewPanelProps {
  groupBy: GroupBy;
  onGroupBy: (group: GroupBy) => void;
  /**
   * Whether every conversation the list can hold carries a job. False leaves
   * Job out of the group options: the read model has no facet for a job-less
   * chat, so grouping by it would drop those rows rather than group them.
   * Not derivable from the job facets: a mixed list has jobs to filter by and
   * still cannot be grouped by them.
   */
  canGroupByJob: boolean;
  /**
   * Put the checkboxes away until the pointer reaches a face, buying back the
   * width they hold. Off, the default, every row keeps its box out, which is
   * what touch has always done since there is no pointer there to reveal
   * anything with.
   */
  compact: boolean;
  onCompact: (compact: boolean) => void;
  /**
   * Whether Compact can do anything here. False on touch, where the boxes stay
   * out whatever it says: with no hover to reveal them, the rule that holds
   * them out is what keeps selection reachable at all (see
   * ChannelPreview.module.css). The row is then not rendered, because a switch
   * that visibly changes nothing teaches people this panel's controls are
   * decoration. The CSS is still the guarantee; leaving the row out only stops
   * the promise being made. Grouping stays either way, so the button keeps its
   * purpose on a phone.
   */
  compactable: boolean;
}

/**
 * How the list is drawn, as against what is in it. Nothing here narrows
 * anything: every chat on screen before a press is on screen after it, which is
 * why grouping left the funnel, whose dot means "you are not seeing all of it".
 */
export function ViewPanel({
  groupBy,
  onGroupBy,
  canGroupByJob,
  compact,
  onCompact,
  compactable,
}: ViewPanelProps) {
  const groups = GROUPS.filter((option) => option.value !== 'job' || canGroupByJob);

  return (
    <div className={styles.panel}>
      {compactable && (
        <section className={styles.section}>
          <p className={styles.heading}>View settings</p>
          {/* A switch, not a checkbox: this is a state the list is in, taking
              effect as it is pressed, rather than an option picked out of a set
              (see `Switch`). The whole row is the label, so the words and the
              track are one target. */}
          <label className={styles.toggleRow}>
            <span className={styles.toggleLabel}>Compact</span>
            <Switch checked={compact} onChange={() => onCompact(!compact)} />
          </label>
        </section>
      )}

      <section className={styles.section}>
        <p className={styles.heading}>Group by</p>
        <div className={styles.segment}>
          {groups.map((option) => (
            <button
              key={option.value}
              type="button"
              className={groupBy === option.value ? styles.segmentActive : styles.segmentButton}
              onClick={() => onGroupBy(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
